import { useQuery } from '@tanstack/react-query';
import { getHandlingTimePerPerson, isGeldigGln, type HandlingTimeBucketSize } from '../handlingTime.api';

/**
 * Story 28-2 — de cijfers per persoon over een gekozen periode.
 *
 * Zonder een volledige periode wordt er niets opgehaald: een standaardperiode verzinnen zou een
 * getal tonen dat niemand gevraagd heeft, en dit is een cijfer waar mensen op beoordeeld worden.
 */
export function useHandlingTimePerPerson(
  period: { from?: string; to?: string },
  // De vergelijking met de vorige periode geeft  mee zodra de eerste vraag al mislukt is of
  // de poort dicht staat. Dan worden de kaarten toch niet getekend, en een tweede vraag stellen die
  // niemand kan zien is één kansloze aanroep per schermweergave (reviewbevinding 27-08-2026).
  // `bucket` vraagt de reeks over de tijd erbij, in DEZELFDE aanroep (story 31.3). Hij hoort in de
  // sleutel, anders zou een vraag mét reeks het antwoord zónder reeks uit de kast pakken — en dan
  // staat de grafiek leeg terwijl de cijfers eronder gewoon kloppen.
  // `userId` beperkt de MÉTING tot één persoon (story 31.4) en hoort daarom óók in de sleutel: zonder
  // hem zou de vraag mét persoon het antwoord over iedereen uit de kast pakken, en dan staat er een
  // grafiek van het hele team onder de melding dat er op één iemand gefilterd is.
  // `gln` kiest de partij (behandeltijd-rapport, CAP-3). Hij hoort in de sleutel, anders zou een ander
  // GLN het antwoord van het vorige GLN uit de cache halen. Een ongeldig GLN doet geen aanroep.
  options: { enabled?: boolean; bucket?: HandlingTimeBucketSize; userId?: number; gln?: string } = {},
) {
  const { from, to } = period;
  const { enabled = true, bucket, userId } = options;
  // Een lege gln is afwezig (alle GLN's), net als in het adres. Hij mag de vraag niet stil uitzetten.
  const gln = options.gln === '' ? undefined : options.gln;
  return useQuery({
    queryKey: ['handling-time', 'per-person', from ?? null, to ?? null, bucket ?? null, userId ?? null, gln ?? null],
    queryFn: () => getHandlingTimePerPerson({ from: from!, to: to! }, { bucket, userId, gln }),
    enabled: enabled && Boolean(from) && Boolean(to) && (gln === undefined || isGeldigGln(gln)),
    staleTime: 60 * 1000,
    // Geen herpoging: de serverlaag doet er ook geen, en een dichte poort of een verkeerde periode
    // wordt niet beter door hem drie keer te stellen.
    retry: false,
  });
}
